import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { EstablishmentService } from '../../../services/establishment/establishment.service';
import { ToastService } from '../../../services/toast/toast.service';
import { LoaderComponent } from '../../../atoms/loader/loader.component';

// the rooms/beds assigned to a booking, grouped by property. When canRelease is
// true (pending booking, can_allocate) each room gets a Release button; releasing
// the last room returns the booking to Pending (the server reverts its lifecycle).
// Closes with true when anything was released so the parent list refreshes.
@Component({
  selector: 'app-assigned-rooms-dialog',
  standalone: true,
  imports: [CommonModule, LoaderComponent],
  templateUrl: './assigned-rooms-dialog.component.html',
  styleUrl: './assigned-rooms-dialog.component.scss',
})
export class AssignedRoomsDialogComponent implements OnInit {
  allocation: any[] = [];
  hasLoaded: boolean = false;
  isLoading: boolean = false;
  private changed: boolean = false;

  constructor(
    private establishmentService: EstablishmentService,
    private toastService: ToastService,
    private dialogRef: MatDialogRef<AssignedRoomsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { bookingId: number; familyCode: string; canRelease: boolean }
  ) {}

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.isLoading = true;
    this.establishmentService.getAllocation(this.data.bookingId).subscribe({
      next: (res: any) => {
        this.allocation = res?.data?.allocation || [];
        this.hasLoaded = true;
        this.isLoading = false;
      },
      error: (err) => {
        this.allocation = [];
        this.hasLoaded = true;
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Failed to load assigned rooms.');
      },
    });
  }

  // one entry per property, each with its rooms and bed lists.
  get assignedGroups(): any[] {
    const byProperty = new Map<number, any>();
    for (const a of this.allocation) {
      const pid = Number(a.propertyId);
      if (!byProperty.has(pid)) {
        byProperty.set(pid, { propertyId: pid, propertyName: a.propertyName, rooms: new Map<number, any>() });
      }
      const rooms = byProperty.get(pid).rooms as Map<number, any>;
      const rid = Number(a.roomId);
      if (!rooms.has(rid)) {
        rooms.set(rid, {
          roomId: rid,
          roomNumber: a.roomNumber,
          roomType: a.roomType,
          capacity: Number(a.capacity) || 0,
          beds: [] as string[],
        });
      }
      rooms.get(rid).beds.push(a.bedNumber);
    }
    return [...byProperty.values()].map((p) => ({
      ...p,
      rooms: [...(p.rooms as Map<number, any>).values()],
    }));
  }

  release(roomId: number): void {
    if (!this.data.canRelease) return;
    this.isLoading = true;
    this.establishmentService.releaseRoom(this.data.bookingId, roomId).subscribe({
      next: (res: any) => {
        this.toastService.success(res?.message || 'Room released.');
        this.changed = true;
        this.load();
      },
      error: (err) => {
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Failed to release room.');
      },
    });
  }

  close(): void {
    this.dialogRef.close(this.changed);
  }
}
